import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Dropdown } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import Scrollbar from 'components/base/Scrollbar';
import { useMemo } from 'react';
import { faRoute } from '@fortawesome/free-solid-svg-icons';
import { routes as sitemapRoutes } from 'sitemap';
import { useAccessToken } from 'providers/AccessTokenProvider';
import { RouteItems, Route } from 'routes/types/types';
import { isRouteVisibleToUser } from 'routes/utils/routeAccess';

type SearchRouteItem = {
  path: string;
  label: string;
  allowed: boolean;
};

const flattenSitemapRoutes = (
  routeItems: RouteItems[],
  hasPermission: (permissionKey: string) => boolean,
  isAdmin: boolean
): SearchRouteItem[] => {
  const out: SearchRouteItem[] = [];

  const visit = (route: Route) => {
    if (!isRouteVisibleToUser(route, isAdmin)) {
      return;
    }

    const allowed =
      isAdmin || !route.permission || hasPermission(route.permission);

    if (route.path) {
      out.push({
        path: route.path,
        label: route.pathName || route.name,
        allowed
      });
    }

    route.pages?.forEach(visit);
  };

  routeItems.forEach(group => group.pages.forEach(visit));

  const seen = new Set<string>();
  return out
    .filter(r => {
      if (seen.has(r.path)) return false;
      seen.add(r.path);
      return true;
    })
    .sort((a, b) => a.path.localeCompare(b.path));
};

const SearchResult = ({
  searchValue = '',
  onSelectItem
}: {
  searchValue?: string;
  onSelectItem?: () => void;
}) => {
  const { hasPermission, isAdmin } = useAccessToken();
  const allRoutes = useMemo(
    () => flattenSitemapRoutes(sitemapRoutes, hasPermission, isAdmin),
    [hasPermission, isAdmin]
  );

  const filteredRoutes = useMemo(() => {
    const q = searchValue.trim().toLowerCase();
    if (!q) return allRoutes;
    return allRoutes.filter(
      r => r.path.toLowerCase().includes(q) || r.label.toLowerCase().includes(q)
    );
  }, [allRoutes, searchValue]);

  return (
    <Scrollbar autoHeight autoHeightMax={'30rem'}>
      <h6 className="text-1000 fs-10 py-2 mb-0 px-3">
        {filteredRoutes.length} <span className="text-500">Routes</span>{' '}
      </h6>
      <div className="py-2">
        {filteredRoutes.map(route => {
          return (
            <Dropdown.Item
              as={Link}
              to={route.path}
              key={route.path}
              onClick={onSelectItem}
              className="py-2"
              disabled={!route.allowed}
            >
              <div className="d-flex align-items-center justify-content-between gap-2 text-1000">
                <div className="d-flex align-items-center fw-normal gap-1">
                  <FontAwesomeIcon icon={faRoute} transform="shrink-2" />
                  <span>{route.label}</span>
                </div>
                <small className="text-500">{route.path}</small>
              </div>
            </Dropdown.Item>
          );
        })}
      </div>
    </Scrollbar>
  );
};

export default SearchResult;
